import { useCallback, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const btn = (bg, fg, br) => ({
  background: bg, color: fg, border: br ? `1px solid ${br}` : "none",
  padding: "0.5rem 1rem", borderRadius: 5,
  fontSize: "0.8rem", fontWeight: 800, cursor: "pointer",
});

const overlay = {
  position: "fixed", inset: 0, background: "rgba(4,10,26,0.88)",
  zIndex: 5000, display: "flex", alignItems: "flex-start",
  justifyContent: "center", padding: "2rem 1rem", overflowY: "auto",
};

const shell = {
  width: "min(820px, 100%)", background: "#0B1A2F",
  border: "1px solid rgba(184,145,90,0.4)", borderRadius: 10,
  padding: "1.6rem 1.8rem", color: "#F5EFE1",
  fontFamily: "'Inter', system-ui, sans-serif",
};


const PhoneDialerModal = ({ open, onClose }) => {
  const [tab, setTab] = useState("dial");
  const [to, setTo] = useState("");
  const [purpose, setPurpose] = useState("ceo_consultation");
  const [varianceId, setVarianceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const [calls, setCalls] = useState([]);
  const [approvals, setApprovals] = useState([]);

  // Variance approval form
  const [approveVarId, setApproveVarId] = useState("");
  const [approveCallId, setApproveCallId] = useState("");
  const [approveNote, setApproveNote] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dialer/calls`, { credentials: "include" }).then(r => r.json()).catch(() => null),
        fetch(`${BACKEND_URL}/api/dialer/variance/approvals`, { credentials: "include" }).then(r => r.json()).catch(() => null),
      ]);
      setCalls(c?.calls || []);
      setApprovals(a?.approvals || []);
    } catch {}
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const normalizePhone = (v) => {
    const digits = (v || "").replace(/[^\d+]/g, "");
    if (digits.startsWith("+")) return digits;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return digits;
  };

  const dial = async () => {
    const num = normalizePhone(to);
    if (!num || num.length < 11) { setErr("Enter a valid phone number (E.164: +18175551234)."); return; }
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/dialer/call`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to_number: num, purpose, linked_variance_id: varianceId || null }),
      });
      const d = await r.json();
      if (!r.ok || d.ok === false) {
        setErr(d.reason || d.detail || `HTTP ${r.status}`);
      } else {
        setResult(d);
        // Pre-fill variance approval form if a variance was linked
        if (varianceId && d.call?.call_id) {
          setApproveVarId(varianceId); setApproveCallId(d.call.call_id);
        }
      }
    } catch (e) { setErr("Network error dialing."); }
    setBusy(false); load();
  };

  const approveVariance = async () => {
    if (!approveVarId || !approveCallId) { setErr("Variance ID + Call ID both required."); return; }
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/dialer/variance/approve`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ variance_id: approveVarId, call_id: approveCallId, note: approveNote }),
      });
      const d = await r.json();
      if (!r.ok || d.ok === false) {
        setErr(d.reason || d.detail || `HTTP ${r.status}`);
      } else {
        alert(`✓ Variance approved\n\nVariance: ${approveVarId}\nSHA-256: ${d.approval?.approval_sha256?.slice(0, 24)}…\n\nUnderwriter + grant-officer audit record stamped.`);
        setApproveVarId(""); setApproveCallId(""); setApproveNote("");
      }
    } catch (e) { setErr("Network error approving."); }
    setBusy(false); load();
  };

  if (!open) return null;

  return (
    <div style={overlay} data-testid="phone-dialer-modal" onClick={onClose}>
      <div style={shell} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
          <div>
            <div style={{ color: "#B8915A", fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase" }}>
              ☎ Internal Phone Dialer · R13
            </div>
            <div style={{ color: "#F5EFE1", fontSize: "1.2rem", fontWeight: 800, marginTop: 2 }}>
              CEO Phone-Consultation Gate
            </div>
            <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginTop: 3 }}>
              Twilio bridges your Google Voice to recipient · every call SHA-256 fingerprinted · variance gate unlocks on connected call
            </div>
          </div>
          <button data-testid="pd-close" onClick={onClose} style={btn("transparent", "#ADBED1", "#334155")}>✕ Close</button>
        </div>

        <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1rem", borderBottom: "1px solid rgba(184,145,90,0.25)" }}>
          {[["dial", "📞 Dial"], ["approve", "✓ Approve Variance"], ["history", "📋 History"]].map(([k, l]) => (
            <button key={k} data-testid={`pd-tab-${k}`} onClick={() => setTab(k)}
                    style={{
                      background: tab === k ? "rgba(184,145,90,0.15)" : "transparent",
                      color: tab === k ? "#F5EFE1" : "#ADBED1",
                      borderBottom: tab === k ? "2px solid #B8915A" : "2px solid transparent",
                      padding: "0.5rem 0.85rem", fontSize: "0.75rem", fontWeight: 700,
                      cursor: "pointer", border: "none",
                    }}>
              {l}
            </button>
          ))}
        </div>

        {err && <div style={{ color: "#FCA5A5", fontSize: "0.8rem", marginBottom: "0.6rem" }}>⚠ {err}</div>}

        {tab === "dial" && (
          <div data-testid="pd-dial-panel">
            <div style={{ marginBottom: "0.8rem" }}>
              <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginBottom: 4 }}>Recipient phone (E.164)</div>
              <input data-testid="pd-to-number" value={to} onChange={e => setTo(e.target.value)}
                     placeholder="+1 817 555 1234"
                     style={{ width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
                              border: "1px solid rgba(184,145,90,0.35)", borderRadius: 5,
                              padding: "0.6rem", fontSize: "1rem", fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8rem", marginBottom: "0.8rem" }}>
              <div>
                <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginBottom: 4 }}>Purpose</div>
                <select data-testid="pd-purpose" value={purpose} onChange={e => setPurpose(e.target.value)}
                        style={{ width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
                                 border: "1px solid rgba(184,145,90,0.35)", borderRadius: 5, padding: "0.5rem" }}>
                  <option value="ceo_consultation">CEO Consultation</option>
                  <option value="expense_variance">Expense Variance Clearance</option>
                  <option value="ada_scope_variance">ADA / Structural Scope Variance</option>
                  <option value="vendor_check_in">Vendor Check-In</option>
                  <option value="owner_update">Owner Update</option>
                </select>
              </div>
              <div>
                <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginBottom: 4 }}>Link to Variance ID (optional)</div>
                <input data-testid="pd-variance-id" value={varianceId} onChange={e => setVarianceId(e.target.value)}
                       placeholder="VAR-2026-0001"
                       style={{ width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
                                border: "1px solid rgba(184,145,90,0.35)", borderRadius: 5, padding: "0.5rem" }} />
              </div>
            </div>
            <button data-testid="pd-dial-btn" onClick={dial} disabled={busy}
                    style={{ ...btn("linear-gradient(90deg,#00DC78,#5AC8D8)", "#062617"),
                             padding: "0.75rem 1.4rem", fontSize: "0.95rem", opacity: busy ? 0.5 : 1 }}>
              {busy ? "Dialing…" : "📞 Dial Now"}
            </button>

            {result && (
              <div data-testid="pd-dial-result" style={{
                marginTop: "1rem", padding: "0.9rem 1.1rem",
                background: result.sandbox_mode ? "rgba(184,145,90,0.14)" : "rgba(0,220,120,0.14)",
                border: `1px solid ${result.sandbox_mode ? "rgba(184,145,90,0.4)" : "rgba(0,220,120,0.4)"}`,
                borderRadius: 6, fontSize: "0.82rem",
              }}>
                <div style={{ color: "#F5EFE1", fontWeight: 700 }}>
                  {result.sandbox_mode ? "🛡 Sandbox — call composed, Twilio not invoked" : "🚀 Live call initiated — your Google Voice is ringing"}
                </div>
                <div style={{ color: "#ADBED1", fontSize: "0.75rem", marginTop: 4, fontFamily: "monospace" }}>
                  call_id: {result.call?.call_id}<br />
                  SID: {result.call?.call_sid || "(sandbox)"}<br />
                  SHA-256: {(result.call?.sha256 || "").slice(0, 32)}…
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "approve" && (
          <div data-testid="pd-approve-panel">
            <div style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(184,145,90,0.2)", borderRadius: 6, padding: "0.9rem 1.1rem", marginBottom: "0.9rem" }}>
              <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>
                Phone-Consultation Gate · 4-step verification
              </div>
              <div style={{ color: "#ADBED1", fontSize: "0.78rem" }}>
                1. Dial recipient · 2. Twilio captures SID + duration + timestamp ·
                3. You click Approve · 4. SHA-256 hash locks the audit stamp
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8rem", marginBottom: "0.8rem" }}>
              <div>
                <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginBottom: 4 }}>Variance ID</div>
                <input data-testid="pd-approve-var-id" value={approveVarId} onChange={e => setApproveVarId(e.target.value)}
                       placeholder="VAR-2026-0001"
                       style={{ width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
                                border: "1px solid rgba(184,145,90,0.35)", borderRadius: 5, padding: "0.55rem" }} />
              </div>
              <div>
                <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginBottom: 4 }}>Call ID (from recent calls)</div>
                <select data-testid="pd-approve-call-id" value={approveCallId} onChange={e => setApproveCallId(e.target.value)}
                        style={{ width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
                                 border: "1px solid rgba(184,145,90,0.35)", borderRadius: 5, padding: "0.55rem" }}>
                  <option value="">— pick a connected call —</option>
                  {calls.slice(0, 20).map(c => (
                    <option key={c.call_id} value={c.call_id}>
                      {c.call_id.slice(0, 20)} · {c.to_number} · {c.duration_seconds || 0}s · {c.status}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: "0.8rem" }}>
              <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginBottom: 4 }}>Note (optional)</div>
              <textarea data-testid="pd-approve-note" value={approveNote} onChange={e => setApproveNote(e.target.value)}
                        rows={3}
                        style={{ width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
                                 border: "1px solid rgba(184,145,90,0.35)", borderRadius: 5, padding: "0.55rem", fontFamily: "inherit" }} />
            </div>
            <button data-testid="pd-approve-btn" onClick={approveVariance} disabled={busy}
                    style={{ ...btn("linear-gradient(90deg,#B8915A,#5AC8D8)", "#0a1929"),
                             padding: "0.75rem 1.4rem", fontSize: "0.9rem", opacity: busy ? 0.5 : 1 }}>
              {busy ? "Stamping…" : "✓ Approve + SHA-256 Stamp"}
            </button>
          </div>
        )}

        {tab === "history" && (
          <div data-testid="pd-history-panel">
            <div style={{ color: "#B8915A", fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 8 }}>
              Recent Calls ({calls.length})
            </div>
            {calls.length === 0 && <div style={{ color: "#7E92A8", fontSize: "0.8rem" }}>No calls yet.</div>}
            {calls.slice(0, 12).map(c => (
              <div key={c.call_id} data-testid={`pd-call-${c.call_id}`}
                   style={{ padding: "0.5rem 0.7rem", borderBottom: "1px solid rgba(184,145,90,0.12)", fontSize: "0.78rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#F5EFE1" }}>{c.to_number} · {c.purpose}</span>
                  <span style={{ color: c.status === "completed" ? "#00DC78" : "#B8915A" }}>{c.status} · {c.duration_seconds || 0}s</span>
                </div>
                <div style={{ color: "#7E92A8", fontSize: "0.68rem", marginTop: 2, fontFamily: "monospace" }}>
                  {c.call_id} · SHA: {(c.sha256 || "").slice(0, 20)}…
                </div>
              </div>
            ))}

            <div style={{ color: "#B8915A", fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", margin: "1.2rem 0 8px" }}>
              Variance Approvals ({approvals.length})
            </div>
            {approvals.length === 0 && <div style={{ color: "#7E92A8", fontSize: "0.8rem" }}>None yet.</div>}
            {approvals.slice(0, 12).map(a => (
              <div key={a.approval_sha256} data-testid={`pd-approval-${a.variance_id}`}
                   style={{ padding: "0.5rem 0.7rem", borderBottom: "1px solid rgba(184,145,90,0.12)", fontSize: "0.78rem" }}>
                <div style={{ color: "#00DC78" }}>✓ {a.variance_id}</div>
                <div style={{ color: "#7E92A8", fontSize: "0.68rem", marginTop: 2, fontFamily: "monospace" }}>
                  call: {a.call_id} · stamp: {(a.approval_sha256 || "").slice(0, 24)}…
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


export default PhoneDialerModal;
